import isDeepEqual from 'fast-deep-equal';
import { type TemporalState, temporal } from 'zundo';
import { create, type StateCreator, type StoreApi } from 'zustand';
import {
  createJSONStorage,
  type PersistOptions,
  type PersistStorage,
  persist
} from 'zustand/middleware';
import { logError } from '../helpers/logger.helper';

const PERSISTED_STORE_KEY_PREFIX = 'moderne-v2:';

type StoreUpdater<T> = {
  update: (update: Partial<T>) => void;
};

export type Store<T> = T & StoreUpdater<T>;

type PersistedStore<T> = [['zustand/persist', T]];
type TemporalStore<T> = [['temporal', StoreApi<TemporalState<T>>]];
type TemporalStateCreator<T> = StateCreator<
  T,
  [['zustand/devtools', never], ['temporal', StoreApi<TemporalState<T>>]]
>;

type Write<T, U> = Omit<T, keyof U> & U;
type WithDevtools<S> = Write<S, [['zustand/devtools', never]]>;

type ReturnedTemporalStore<T> = Write<
  WithDevtools<StoreApi<Store<T>>>,
  { temporal: StoreApi<TemporalState<T>> }
>;

export function createTemporalStore<T>(
  obj: TemporalStateCreator<T>,
  fields: (keyof T)[]
) {
  return create<Store<T>, TemporalStore<Store<T>>>(
    temporal(
      (set, get, store: ReturnedTemporalStore<T>) => {
        return {
          ...obj(set, get, store),
          update: (update) => {
            store.temporal.getState().pause();
            set(
              (state) =>
                ({
                  ...state,
                  ...update
                }) as Store<T>
            );
          }
        };
      },
      {
        partialize: (state) => {
          return fields.reduce(
            (acc, key) => {
              acc[key] = state[key];
              return acc;
            },
            {} as Partial<T>
          );
        },
        equality: isDeepEqual,
        limit: 10
      }
    )
  );
}

/**
 * Makes a storage's writes non-fatal. zustand commits the state change and
 * *then* writes, rethrowing a failed `setItem` out of whatever action triggered
 * it — so a quota-exhausted browser turns any store update into an uncaught
 * error. `createJSONStorage` already swallows a denied *access*; this covers the
 * denied *write*. State still updates in memory; only durability is lost.
 *
 * Wraps rather than replaces, so a store supplying its own storage (see
 * `organization-store`, which writes to session + local on every org change)
 * gets the same tolerance instead of opting out of it.
 */
const writeTolerant = <S>(
  storage: PersistStorage<S> | undefined
): PersistStorage<S> | undefined => {
  if (!storage) {
    return undefined;
  }
  return {
    ...storage,
    setItem: (name, value) => {
      try {
        storage.setItem(name, value);
      } catch (error) {
        logError({
          name: 'PersistedStoreWriteFailed',
          message: `${name}: ${
            error instanceof Error ? error.message : String(error)
          }`
        });
      }
    }
  };
};

export function createPersistedStore<T>(
  obj: StateCreator<T>,
  options: PersistOptions<Store<T>>,
  initialization?: (o: T) => T
) {
  return create<Store<T>, PersistedStore<Store<T>>>(
    persist(
      (set, get, state) => {
        const migratedState = initialization?.(obj(set, get, state));

        return {
          ...(migratedState || obj(set, get, state)),
          update: (update) =>
            set(
              (state) =>
                ({
                  ...state,
                  ...update
                }) as Store<T>
            )
        };
      },
      {
        ...options,
        name: `${PERSISTED_STORE_KEY_PREFIX}${options.name}`,
        storage: writeTolerant(
          options.storage ?? createJSONStorage<Store<T>>(() => localStorage)
        )
      }
    )
  );
}

export function createStore<T>(obj: StateCreator<T>) {
  return create<Store<T>>((set, get, store) => {
    return {
      ...obj(set, get, store),
      update: (update) =>
        set(
          (state) =>
            ({
              ...state,
              ...update
            }) as Store<T>
        )
    };
  });
}
